import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, Badge } from '../ui';
import { formatRelativeTime } from '../../utils/dateUtils';
import { GTMProject, STAGE_LABELS } from '../../types/pipeline';

interface BoardCardProps {
  project: GTMProject;
  onClick: (project: GTMProject) => void;
  /** Show the stage chip — used in the "Needs you" lane where cards span stages. */
  showStage?: boolean;
}

export const BoardCard: React.FC<BoardCardProps> = ({ project, onClick, showStage = false }) => {
  return (
    <Card padding="sm" className="mb-2" onClick={() => onClick(project)}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-neutral-900 line-clamp-2">{project.name}</span>
        {project.needs_human && (
          <span title={project.needs_human_reason || 'Needs attention'} className="flex-shrink-0">
            <AlertTriangle
              className="w-4 h-4 text-warning-500"
              // The reason rides the label too — a title attribute is
              // mouse-only, so assistive tech would otherwise never hear it.
              aria-label={
                project.needs_human_reason
                  ? `Needs attention: ${project.needs_human_reason}`
                  : 'Needs attention'
              }
            />
          </span>
        )}
      </div>

      {project.client_entity_name && (
        <p className="text-xs text-neutral-500 mt-0.5 truncate">{project.client_entity_name}</p>
      )}

      <div className="flex items-center justify-between mt-2 gap-2">
        {showStage && <Badge variant="neutral" size="sm">{STAGE_LABELS[project.pipeline_stage]}</Badge>}
        <span className="text-2xs text-neutral-400 ml-auto">
          {formatRelativeTime(project.updated_at)}
        </span>
      </div>
    </Card>
  );
};
